import { Component } from '@theme/component';

/**
 * Public (unauthenticated) Trusted Shops REST API.
 * Keyed by the shop's public Trusted Shops ID (TSID) — no secret involved.
 * @see https://developers.etrusted.com/
 */
const TRUSTED_SHOPS_API_BASE = 'https://api.trustedshops.com/rest/public/v2/shops';

/** Same path as the theme's `full-star` icon (snippets/icon.liquid). */
const STAR_PATH =
  'M6.095.345a.5.5 0 0 1 .95 0l1.21 3.72a.5.5 0 0 0 .475.345h3.91a.5.5 0 0 1 .294.904L9.77 7.613a.5.5 0 0 0-.181.559l1.208 3.719a.5.5 0 0 1-.77.559L6.865 10.15a.5.5 0 0 0-.588 0L3.113 12.45a.5.5 0 0 1-.77-.56l1.209-3.718a.5.5 0 0 0-.182-.56L.207 5.315A.5.5 0 0 1 .5 4.41h3.91a.5.5 0 0 0 .475-.346L6.095.345Z';

const SVG_NS = 'http://www.w3.org/2000/svg';

/**
 * @typedef {object} ApcReviewsFeedRefs
 * @property {HTMLElement} summary - Aggregate rating summary wrapper
 * @property {HTMLElement} score - Aggregate score element
 * @property {HTMLElement} summaryStars - Star row in the summary
 * @property {HTMLElement} count - "Based on N reviews" element
 * @property {HTMLElement} grid - Review cards grid
 * @property {HTMLElement} [actions] - "Load more" wrapper
 * @property {HTMLButtonElement} [loadMore] - "Load more" button
 * @property {HTMLElement} status - Loading / error status element
 */

/**
 * Fetches shop reviews from the public Trusted Shops API and renders them
 * into the server-rendered shell of `sections/apc-reviews-feed.liquid`.
 *
 * @extends {Component<ApcReviewsFeedRefs>}
 */
class ApcReviewsFeed extends Component {
  requiredRefs = ['summary', 'score', 'summaryStars', 'count', 'grid', 'status'];

  #abortController = new AbortController();

  /** @type {{ tsid: string, count: number, minRating: string, locale: string, translations: Record<string, string>, reviewsData: { reviews: Array<{ rating: number, comment: string, date: string }> } | null }} */
  #config = { tsid: '', count: 12, minRating: '0', locale: 'en', translations: {}, reviewsData: null };

  /** @type {Array<{ mark: number, comment: string, date: string }>} */
  #reviews = [];

  /** Number of reviews currently rendered (grows via loadMore). */
  #shown = 0;

  connectedCallback() {
    super.connectedCallback();

    const configScript = this.querySelector('script[type="application/json"]');

    try {
      this.#config = { ...this.#config, ...JSON.parse(configScript?.textContent || '{}') };
    } catch {
      // Malformed config — leave defaults; the fetch below will fail gracefully.
    }

    this.#load();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  async #load() {
    const { tsid } = this.#config;

    if (!tsid) {
      this.#showError();
      return;
    }

    try {
      const [quality, reviews] = await Promise.all([this.#fetchQuality(tsid), this.#getReviews(tsid)]);

      this.#renderSummary(quality);

      const minRating = Number(this.#config.minRating) || 0;
      this.#reviews = reviews.filter((review) => review.mark >= minRating);
      this.#shown = 0;
      this.refs.grid.replaceChildren();
      this.#renderNextPage();

      this.refs.status.hidden = true;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      this.#showError();
    }
  }

  /**
   * Uses cached reviews from the shop metafield (passed via config from Liquid).
   * Falls back to the public feed (recent reviews only) when the cache is empty.
   * @param {string} tsid
   * @returns {Promise<Array<{ mark: number, comment: string, date: string }>>}
   */
  async #getReviews(tsid) {
    const cached = this.#getCachedReviews();
    if (cached.length) return cached;
    return this.#fetchPublicReviews(tsid);
  }

  /**
   * Extracts cached reviews from config (synced from metafield via Liquid).
   * @returns {Array<{ mark: number, comment: string, date: string }>}
   */
  #getCachedReviews() {
    const data = this.#config.reviewsData;
    const reviews = Array.isArray(data?.reviews) ? data.reviews : [];
    return reviews.map((review) => ({
      mark: Number(review.rating) || 0,
      comment: (review.comment || '').trim(),
      date: review.date || '',
    }));
  }

  /**
   * Fetches the aggregate quality indicators (overall mark + review count).
   * @param {string} tsid
   * @returns {Promise<{ overallMark: number, reviewCount: number }>}
   */
  async #fetchQuality(tsid) {
    const data = await this.#fetchJson(`${TRUSTED_SHOPS_API_BASE}/${encodeURIComponent(tsid)}/quality/reviews.json`);
    const indicator = data?.response?.data?.shop?.qualityIndicators?.reviewIndicator || {};

    return {
      overallMark: Number(indicator.overallMark) || 0,
      reviewCount: Number(indicator.activeReviewCount) || 0,
    };
  }

  /**
   * Fetches recent reviews from the public (unauthenticated) feed. Used as a
   * fallback when the cached asset is empty — this feed only returns the most
   * recent handful, so it cannot paginate through the full history.
   * @param {string} tsid
   * @returns {Promise<Array<{ mark: number, comment: string, date: string }>>}
   */
  async #fetchPublicReviews(tsid) {
    const data = await this.#fetchJson(`${TRUSTED_SHOPS_API_BASE}/${encodeURIComponent(tsid)}/reviews.json`);
    const reviews = data?.response?.data?.shop?.reviews || [];

    return reviews
      .map((review) => ({
        mark: Number(review.mark) || 0,
        comment: (review.comment || '').trim(),
        date: review.confirmationDate || review.creationDate || review.changeDate || '',
      }))
      .filter((review) => review.comment);
  }

  /**
   * @param {string} url
   * @returns {Promise<any>}
   */
  async #fetchJson(url) {
    const response = await fetch(url, { signal: this.#abortController.signal });

    if (!response.ok) {
      throw new Error(`Trusted Shops API error: ${response.status}`);
    }

    return response.json();
  }

  /**
   * @param {{ overallMark: number, reviewCount: number }} quality
   */
  #renderSummary({ overallMark, reviewCount }) {
    if (!reviewCount) return;

    const { translations } = this.#config;
    const average = overallMark.toLocaleString(this.#config.locale, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });

    this.refs.score.textContent = average;
    this.refs.summaryStars.replaceChildren(this.#buildStars(overallMark));
    this.refs.summaryStars.setAttribute(
      'aria-label',
      (translations.ratingLabel || '[rating]').replace('[rating]', average),
    );
    this.refs.count.textContent = (translations.basedOnCount || '[count]').replace('[count]', String(reviewCount));
    this.refs.summary.hidden = false;
  }

  /** Renders the next page of reviews and updates the "Load more" control. */
  #renderNextPage() {
    const pageSize = Number(this.#config.count) || 12;
    const next = this.#reviews.slice(this.#shown, this.#shown + pageSize);

    this.refs.grid.append(...next.map((review) => this.#buildCard(review)));
    this.#shown += next.length;

    const hasMore = this.#shown < this.#reviews.length;
    if (this.refs.actions) this.refs.actions.hidden = !hasMore;
  }

  /** Public handler wired via `on:click="/loadMore"` on the button. */
  loadMore() {
    this.#renderNextPage();
  }

  /**
   * @param {{ mark: number, comment: string, date: string }} review
   * @returns {HTMLElement}
   */
  #buildCard(review) {
    const { translations } = this.#config;

    const card = document.createElement('article');
    card.className = 'apc-reviews-feed__card';

    const meta = document.createElement('div');
    meta.className = 'apc-reviews-feed__card-meta';

    const stars = document.createElement('div');
    stars.className = 'apc-reviews-feed__stars';
    stars.setAttribute('aria-label', (translations.ratingLabel || '[rating]').replace('[rating]', String(review.mark)));
    stars.append(this.#buildStars(review.mark));
    meta.append(stars);

    if (review.date) {
      const date = document.createElement('time');
      date.className = 'apc-reviews-feed__card-date';
      date.dateTime = review.date;
      date.textContent = this.#formatDate(review.date);
      meta.append(date);
    }

    const text = document.createElement('p');
    text.className = 'apc-reviews-feed__card-text';
    text.textContent = review.comment;

    const verified = document.createElement('span');
    verified.className = 'apc-reviews-feed__card-verified';
    verified.textContent = translations.verified || '';

    card.append(meta, text, verified);
    return card;
  }

  /**
   * Builds a 5-star row, dimming the stars above the given rating.
   * @param {number} rating
   * @returns {DocumentFragment}
   */
  #buildStars(rating) {
    const fragment = document.createDocumentFragment();
    const rounded = Math.round(rating);

    for (let i = 1; i <= 5; i += 1) {
      const svg = document.createElementNS(SVG_NS, 'svg');
      svg.setAttribute('viewBox', '0 0 14 13');
      svg.setAttribute('fill', 'none');
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');

      if (i > rounded) {
        svg.classList.add('apc-reviews-feed__star--empty');
      }

      const path = document.createElementNS(SVG_NS, 'path');
      path.setAttribute('d', STAR_PATH);
      path.setAttribute('fill', 'currentColor');
      svg.append(path);
      fragment.append(svg);
    }

    return fragment;
  }

  /**
   * @param {string} isoDate
   * @returns {string}
   */
  #formatDate(isoDate) {
    const date = new Date(isoDate);

    if (Number.isNaN(date.getTime())) return '';

    return date.toLocaleDateString(this.#config.locale, { year: 'numeric', month: 'long', day: 'numeric' });
  }

  #showError() {
    this.refs.status.textContent = this.#config.translations.error || '';
  }
}

if (!customElements.get('apc-reviews-feed')) {
  customElements.define('apc-reviews-feed', ApcReviewsFeed);
}
